import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { IItemForGetShortInfo, UserRoles, IGetRestaurantsShort } from 'types';
import { getRestaurantsByCategoryShortInfo } from 'backend/restaurants';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import { MultipleSelect } from './MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './index.module.scss';

interface IProps {
	setIDsRestaurant: (value: string[]) => void;
	setFetchedRestaurants: (value: IItemForGetShortInfo[]) => void;
	changeLoaderStatus: (value: boolean) => void;
	initialIDsSelectedRestaurant: string[];
	IDSelectedCategory: Nullable<string>;
	IDsSelectedRestaurant: string[];
}

export const RestaurantsByCategoryMultipleSelect: React.FC<IProps> = React.memo(
	({
		setIDsRestaurant,
		setFetchedRestaurants,
		changeLoaderStatus,
		initialIDsSelectedRestaurant,
		IDSelectedCategory,
		IDsSelectedRestaurant,
	}) => {
		const { loggedUser } = useContext<IAppContext>(AppContext);
		const hasAccess = useRoleAccess([
			UserRoles.SUPER_USER,
			UserRoles.CLIENT_MANAGER,
			UserRoles.GROUP_MANAGER,
			UserRoles.TECHNICAL,
		]);
		const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
		const { response, loading, refetch, responseError } = useFetchData<IGetRestaurantsShort, string>(
			IDSelectedCategory ? getRestaurantsByCategoryShortInfo : null,
			IDSelectedCategory || '',
		);

		useEffect(() => {
			refetch();
		}, [IDSelectedCategory]);

		const restaurants = useMemo(() => {
			if (hasAccess && response) {
				return response.data.restaurants;
			}
			if (!hasAccess && response) {
				const accessibleRestaurantIDs = loggedUser.restaurants ? loggedUser.restaurants.map((el) => el.id) : [];
				return response.data.restaurants.filter((restaurant) =>
					accessibleRestaurantIDs.includes(restaurant.id),
				);
			}
		}, [hasAccess, response, loggedUser]);

		const handleRemove = useCallback(
			(id: string) => {
				const updatedSelectedItems = IDsSelectedRestaurant.filter((el) => el !== id);
				setIDsRestaurant(updatedSelectedItems);
			},
			[setIDsRestaurant, IDsSelectedRestaurant],
		);

		const restaurantsList = useMemo(() => {
			return IDsSelectedRestaurant.map((el) => {
				const label = response?.data.restaurants.find((item) => item.id === el)?.name || '';
				const accessibleRestaurantIds = loggedUser.restaurants?.map((el) => el.id);
				return (
					<SelectedItem
						key={el}
						label={label}
						onDelete={
							!hasAccess && !accessibleRestaurantIds?.includes(el) ? undefined : () => handleRemove(el)
						}
					/>
				);
			});
		}, [IDsSelectedRestaurant, response, loggedUser, hasAccess, handleRemove]);

		useEffect(() => {
			if (response) {
				const accessibleRestaurants = loggedUser.restaurants ? loggedUser.restaurants : [];
				const restaurants = hasAccess ? response.data.restaurants : accessibleRestaurants;
				setFetchedRestaurants(restaurants);
			}
			if (isFirstRender && response) {
				setIDsRestaurant(initialIDsSelectedRestaurant);
				changeLoaderStatus(false);
				setIsFirstRender(false);
			}
		}, [response, loggedUser, hasAccess, initialIDsSelectedRestaurant]);

		return (
			<div className={style.container} data-test="RestaurantsMultipleSelect">
				<div className={style.field}>
					<MultipleSelect
						data={restaurants || []}
						isLoading={loading}
						refetch={refetch}
						responseError={responseError}
						setIDsRestaurant={setIDsRestaurant}
						IDSelectedCustomer={IDSelectedCategory}
						IDsSelectedRestaurant={IDsSelectedRestaurant}
					/>
				</div>
				<div className={style.groupsList}>{restaurantsList}</div>
			</div>
		);
	},
);
